"use client"

import { useLocalStorage } from "@opensea/ui-kit/hooks"
import {
  type IMutableContext,
  useUnleashClient,
  useUnleashContext,
} from "@unleash/nextjs/client"
import { assign } from "es-toolkit/compat"
import { omit } from "es-toolkit/object"
import { isEqual } from "es-toolkit/predicate"
import { useEffect, useRef } from "react"

type Properties = NonNullable<IMutableContext["properties"]>

type Props = {
  /**
   * Sets a boolean representing whether the flags have been fetched using the
   * updated context and are ready to be used.
   */
  setReady: (ready: boolean) => void
}

export function useFlagContext({ setReady }: Props) {
  const updateUnleashContext = useUnleashContext()
  const unleashClient = useUnleashClient()
  const unleashClientStartedRef = useRef(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Context properties are persisted in local storage and shared across tabs
  const [contextProperties, setContextProperties] = useLocalStorage<Properties>(
    "use-flag-context-properties",
    unleashClient.getContext().properties ?? {},
  )

  // Get the previous context from the unleash client omitting static fields
  function getPreviousContext() {
    const currentContext = unleashClient.getContext()
    return omit(currentContext, ["appName", "environment"])
  }

  // Update the unleash flag context with the current context properties
  useEffect(() => {
    void updateUnleashContext({
      ...getPreviousContext(),
      properties: contextProperties,
    })
  }, [contextProperties])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [])

  // Update the context properties by merging the current context properties with the new properties
  function updateContextProperties(updates: Properties) {
    const updated = assign(contextProperties, updates)
    if (isEqual(contextProperties, updated)) {
      return // Prevent unnecessary refetch of flags
    }
    setContextProperties(updated)
  }

  // Start the unleash client if it's not already started
  function startUnleashClient() {
    if (!unleashClientStartedRef.current) {
      void unleashClient.start().then(() => {
        // Clear any existing timeout before setting a new one
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
          setReady(true)
          timeoutRef.current = null
        }, 1000)
      })
      unleashClientStartedRef.current = true
    }
  }

  // Update the user ID in the context then start the unleash client if it's not already started
  function setUserId(userId: string | undefined) {
    const previousContext = getPreviousContext()

    // Set userId and start the unleash client if it's not already started
    if (previousContext.userId !== userId) {
      void updateUnleashContext({
        ...previousContext,
        userId,
      }).then(startUnleashClient)
      return
    }

    // Start the unleash client with userId undefined (no wallet connected) if it's not already started
    if (previousContext.userId === undefined) {
      startUnleashClient()
      return
    }
  }

  return {
    /**
     * Update the flag context properties by merging the current context properties with the new properties.
     */
    updateContextProperties,
    setUserId,
  }
}
